import { PageDialog } from "@/components/dialog/PageDialog"
import { QueryRender } from "@/components/helpers/QueryRender"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import { useAbsenceRequests } from "@/entities/absenceRequest/useAbsenceRequest"
import { useUsers } from "@/entities/user/useUser"
import { AbsenceRequestContent } from "@/pages/AbsenceRequest"
import { YoungsFilterSearchBar } from "@/components/filters/YoungsFilterSearchBar"
import { SchoolPeriodSelector } from "@/components/filters/SchoolPeriodSelector"
import { AbsenceRequestFilterBar } from "@/components/filters/AbsenceRequestFilterBar"
import { SchoolPeriod } from "@/entities/schoolYear/SchoolPeriod"
import { AbsenceRequestStatus } from "@/entities/absenceRequest/absenceRequest"
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   useReactTable,
} from "@tanstack/react-table"
import { isWithinInterval } from "date-fns"

import { useMemo, useState } from "react"
import { sortByFrom } from "./absences-et-retards"
import { formatPeriodSmall, getStatusBadge } from "@/utils/commonAbsenceRequestUi"
import { RecurrencePeriod, recurrencePeriodTranslations } from "@/types/recurrence"

export default function AbsenceRequests() {
   const [selectedPeriod, setSelectedPeriod] = useState<SchoolPeriod | null>(null)
   const [searchText, setSearchText] = useState("")
   const [selectedGroups, setSelectedGroups] = useState<string[]>([])
   const [selectedStatus, setSelectedStatus] = useState<AbsenceRequestStatus | "all">("pending")

   const { data: absenceRequests, ...query } = useAbsenceRequests({})
   const { data: users } = useUsers()

   const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

   const filteredAbsenceRequests = useMemo(() => {
      const requests = sortByFrom(absenceRequests) || []
      return requests.filter((request) => {
         if (
            selectedPeriod &&
            !isWithinInterval(request.from, {
               start: selectedPeriod.start_date,
               end: selectedPeriod.end_date,
            })
         )
            return false

         if (selectedStatus !== "all" && request.status !== selectedStatus) return false

         const user = users?.find((u) => u.id === request.young_id)
         if (!user) return false

         if (!selectedGroups.includes(user.group)) return false

         if (!searchText) return true
         const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
         return fullName.includes(searchText.toLowerCase())
      })
   }, [absenceRequests, users, selectedGroups, searchText, selectedPeriod, selectedStatus])

   const columnHelper = createColumnHelper<any>()
   const columns = useMemo(
      () => [
         columnHelper.accessor("young_id", {
            header: "Jeune",
            cell: (info) => {
               const user = users?.find((u) => u.id === info.getValue())
               return user ? <UserSmallProfile user={user} /> : info.getValue()
            },
         }),
         columnHelper.accessor((row) => ({ from: row.from, to: row.to, is_recurring: row.is_recurring, recurring_period: row.recurring_period }), {
            id: "period",
            header: "Période",
            cell: (info) => {
               const { from, to } = info.getValue()
               const date = formatPeriodSmall(from, to)
               if (info.getValue().is_recurring) {
                  const rec = recurrencePeriodTranslations[(info.getValue().recurring_period as RecurrencePeriod)]
                  return `${date} (chaque ${rec})`
               }
               return date
            },
         }),
         columnHelper.accessor("reason", {
            header: "Motif",
            cell: (info) => (
               <div className="max-w-xs truncate" title={info.getValue()}>
                  {info.getValue()}
               </div>
            ),
         }),
         columnHelper.accessor("status", {
            header: "Statut",
            cell: (info) => getStatusBadge(info.getValue(), false)
         }),
      ],
      [users],
   )

   const table = useReactTable({
      data: filteredAbsenceRequests,
      columns,
      getCoreRowModel: getCoreRowModel(),
   })

   return (
      <>
         <div className="page-section">
            <div className="">
               <h1 className="title">Demandes d'absence</h1>

               <div className="space-y-4 mb-6">
                  <SchoolPeriodSelector setSelectedSchoolPeriod={setSelectedPeriod} />
                  <YoungsFilterSearchBar
                     searchText={searchText}
                     setSearchText={setSearchText}
                     selectedGroups={selectedGroups}
                     setSelectedGroups={setSelectedGroups}
                  />
                  <AbsenceRequestFilterBar
                     selectedStatus={selectedStatus}
                     setSelectedStatus={setSelectedStatus}
                  />
               </div>

               <QueryRender query={query} data={absenceRequests}>
                  {() => (
                     <>
                        <div className="hidden md:block overflow-x-auto">
                           <table className="table">
                              <thead>
                                 {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                       {headerGroup.headers.map((header) => (
                                          <th key={header.id}>
                                             {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                     header.column.columnDef.header,
                                                     header.getContext(),
                                                  )}
                                          </th>
                                       ))}
                                    </tr>
                                 ))}
                              </thead>
                              <tbody>
                                 {table.getRowModel().rows.map((row) => (
                                    <tr
                                       key={row.id}
                                       className="hover cursor-pointer"
                                       onClick={() => setSelectedRequestId(row.original.id)}
                                    >
                                       {row.getVisibleCells().map((cell) => (
                                          <td key={cell.id}>
                                             {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>

                        <div className="md:hidden grid grid-cols-1 gap-4">
                           {table.getRowModel().rows.map((row) => {
                              return (
                                 <div
                                    key={row.id}
                                    className="card bg-base-100 shadow-md hover:shadow-lg cursor-pointer transition-shadow"
                                    onClick={() => setSelectedRequestId(row.original.id)}
                                 >
                                    <div className="card-body p-4 md:p-8">
                                       <div className="flex flex-col gap-2">
                                          <AbsenceRequestContent
                                             id={row.original.id}
                                             variant="small"
                                          />
                                       </div>
                                    </div>
                                 </div>
                              )
                           })}
                        </div>
                     </>
                  )}
               </QueryRender>
            </div>
         </div>

         <PageDialog isOpen={!!selectedRequestId} onClose={() => setSelectedRequestId(null)}>
            {selectedRequestId && <AbsenceRequestContent id={selectedRequestId} variant="full" />}
         </PageDialog>
      </>
   )
}
